import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import moment from "moment-timezone";
import {
  ArrowLeftIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";
import { AddressAutocompleteInput } from "components/AddressAutocompleteInput";
import {
  addHoursToLocalString,
  DateTimePicker,
  fromLocalString,
  toLocalString,
} from "components/DateTimePicker";
import { Switch } from "components/switch";
import { ToastError } from "utils/toasts";
import {
  createTrip,
  fetchConfigurations,
  fetchCooperateDetails,
} from "services/memberMrn";
import { buildCreateTripPayload } from "services/tripPayload";

export function findServiceAreaId(zip, serviceAreas = []) {
  if (!zip) return null;
  const cleanZip = zip.trim();
  const matched = serviceAreas.find((sa) => sa.zipCodes?.includes(cleanZip));
  return matched ? matched._id : null;
}

const labelClass = "block text-xs font-semibold text-gray-500 mb-1.5";

function fieldClass(hasError) {
  return `w-full rounded-2xl bg-gray-100 px-4 py-3.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:bg-white transition disabled:opacity-50 ${
    hasError ? "ring-2 ring-red-400" : "focus:ring-[#293562]"
  }`;
}

function selectClass(hasError) {
  return `${fieldClass(hasError)} appearance-none pr-10`;
}

// Preloads whatever member info we already have (from CreateMember or the
// member's prior trips) so staff don't retype the same details every time.
// `timeZone` is the member's IANA zone — defaults to "right now" as it
// reads on the member's clock, not the staff browser's local clock, since
// that's how the picker's value is later interpreted when building the
// API payload.
function buildDefaultValues(member, timeZone) {
  const now = timeZone
    ? moment().tz(timeZone).format("YYYY-MM-DDTHH:mm")
    : toLocalString(new Date());
  return {
    levelOfService: "",
    spaceType: "",
    displayName: member?.displayName || "",
    mobileNumber: member?.contactNumber || "",
    authorizationNumber: "",
    pickupAddress: member?.address || "",
    pickupAddressLat: member?.addressLat ?? null,
    pickupAddressLng: member?.addressLng ?? null,
    pickupAddressZip: member?.addressZip || "",
    serviceArea: null,
    pickupDateTime: now,
    dropoffTime: addHoursToLocalString(now, 1),
    dropoffAddress: "",
    dropoffAddressLat: null,
    dropoffAddressLng: null,
    dropoffAddressZip: "",
    serviceAreaDest: null,
    driverNotes: "",
    isReturnTrip: false,
    returnPickupAddress: "",
    returnPickupAddressLat: null,
    returnPickupAddressLng: null,
    returnPickupAddressZip: "",
    returnServiceArea: null,
    returnPickupDateTime: "",
    returnIsWillCall: false,
    returnDropoffAddress: "",
    returnDropoffAddressLat: null,
    returnDropoffAddressLng: null,
    returnDropoffAddressZip: "",
    returnServiceAreaDest: null,
  };
}

function SectionHeader({ title }) {
  return (
    <div className="flex items-center gap-3 pt-2 first:pt-0">
      <h3 className="text-sm font-semibold text-gray-900 shrink-0">
        {title}
      </h3>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

function FormField({ label, required, error, children }) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && " *"}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function SelectField({
  label,
  required,
  error,
  registration,
  options,
  placeholder,
  disabled,
}) {
  return (
    <FormField label={label} required={required} error={error}>
      <div className="relative">
        <select
          {...registration}
          defaultValue=""
          disabled={disabled}
          className={selectClass(!!error)}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => {
            const isObject = typeof option === "object" && option !== null;
            const optionValue = isObject ? option.value : option;
            const optionLabel = isObject ? option.label : option;
            return (
              <option key={optionValue} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      </div>
    </FormField>
  );
}

function ServiceSection({
  register,
  errors,
  showHeader,
  spaceTypeOptions,
  configsLoading,
  showAuthorizationNumber,
}) {
  return (
    <>
      {showHeader && <SectionHeader title="Service & Member" />}
      <div className="mb-3">
        <SelectField
          label="Space Type"
          required
          error={errors.spaceType?.message}
          registration={register("spaceType", {
            required: "Space type is required",
          })}
          options={spaceTypeOptions}
          disabled={configsLoading}
          placeholder={configsLoading ? "Loading..." : "-- Select --"}
        />
      </div>

      <FormField label="Display Name" required error={errors.displayName?.message}>
        <input
          {...register("displayName", { required: "Display name is required" })}
          readOnly
          placeholder="John Doe"
          className={fieldClass(!!errors.displayName)}
        />
      </FormField>

      <FormField label="Mobile Number" required error={errors.mobileNumber?.message}>
        <input
          type="tel"
          inputMode="numeric"
          onKeyDown={(e) => {
            if (
              e.key.length === 1 &&
              !/[0-9]/.test(e.key) &&
              !e.ctrlKey &&
              !e.metaKey
            ) {
              e.preventDefault();
            }
          }}
          {...register("mobileNumber", {
            required: "Mobile number is required",
            validate: (value) => {
              const digits = value.replace(/\D/g, "");
              if (digits.length < 10 || digits.length > 11) {
                return "Mobile number must be 10-11 digits";
              }
              return true;
            },
          })}
          placeholder="(555) 000-0000"
          className={fieldClass(!!errors.mobileNumber)}
        />
      </FormField>

      {showAuthorizationNumber && (
        <FormField
          label="Authorization Number"
          error={errors.authorizationNumber?.message}
        >
          <input
            {...register("authorizationNumber")}
            placeholder="Authorization Number"
            className={fieldClass(!!errors.authorizationNumber)}
          />
        </FormField>
      )}
    </>
  );
}

function PickupSection({
  control,
  setValue,
  watch,
  errors,
  showHeader,
  serviceAreas,
}) {
  return (
    <>
      {showHeader && <SectionHeader title="Pickup" />}
      <FormField label="Pickup Address" required error={errors?.pickupAddress?.message}>
        <Controller
          name="pickupAddress"
          control={control}
          rules={{
            required: "Address is required",
            validate: () =>
              (watch("pickupAddressLat") != null && watch("pickupAddressLng") != null) ||
              "Please select an address from the dropdown",
          }}
          render={({ field }) => (
            <AddressAutocompleteInput
              value={field.value}
              lat={watch("pickupAddressLat")}
              lng={watch("pickupAddressLng")}
              onChange={({ address, lat, lng, components }) => {
                setValue("pickupAddressLat", lat);
                setValue("pickupAddressLng", lng);
                setValue("pickupAddressZip", components?.zipCode || "");
                setValue("serviceArea", findServiceAreaId(components?.zipCode, serviceAreas));
                field.onChange(address);
              }}
              placeholder="Enter a location"
              className={fieldClass(!!errors?.pickupAddress)}
            />
          )}
        />
      </FormField>

      <FormField label="Pickup Date & Time" required error={errors?.pickupDateTime?.message}>
        <Controller
          name="pickupDateTime"
          control={control}
          rules={{
            required: "Pickup time is required",
            validate: (value) => {
              if (!value) return true;
              const fiveMinsAgo = new Date(Date.now() - 5 * 60 * 1000);
              return (
                fromLocalString(value) >= fiveMinsAgo ||
                "Pickup time cannot be in the past"
              );
            },
          }}
          render={({ field }) => (
            <DateTimePicker
              value={field.value}
              minDate={new Date()}
              onChange={(value) => {
                field.onChange(value);
                setValue("dropoffTime", addHoursToLocalString(value, 1));
              }}
              placeholder="Select pickup date & time"
              className={fieldClass(!!errors?.pickupDateTime)}
              hasError={!!errors?.pickupDateTime}
            />
          )}
        />
      </FormField>
    </>
  );
}

function DropoffSection({
  control,
  setValue,
  watch,
  errors,
  showHeader,
  serviceAreas,
}) {
  return (
    <>
      {showHeader && <SectionHeader title="Dropoff" />}
      <FormField label="Dropoff Address" required error={errors.dropoffAddress?.message}>
        <Controller
          name="dropoffAddress"
          control={control}
          rules={{
            required: "Address is required",
            validate: () =>
              (watch("dropoffAddressLat") != null && watch("dropoffAddressLng") != null) ||
              "Please select an address from the dropdown",
          }}
          render={({ field }) => (
            <AddressAutocompleteInput
              value={field.value}
              lat={watch("dropoffAddressLat")}
              lng={watch("dropoffAddressLng")}
              onChange={({ address, lat, lng, components }) => {
                setValue("dropoffAddressLat", lat);
                setValue("dropoffAddressLng", lng);
                setValue("dropoffAddressZip", components?.zipCode || "");
                setValue("serviceAreaDest", findServiceAreaId(components?.zipCode, serviceAreas));
                field.onChange(address);
              }}
              placeholder="Enter a location"
              className={fieldClass(!!errors.dropoffAddress)}
            />
          )}
        />
      </FormField>

      <FormField
        label="Dropoff Date & Time"
        required
        error={errors.dropoffTime?.message}
      >
        <Controller
          name="dropoffTime"
          control={control}
          rules={{
            required: "Dropoff time is required",
            validate: (value) => {
              const pickup = watch("pickupDateTime");
              if (!value || !pickup) return true;
              return (
                value >= pickup || "Dropoff time can't be before pickup time"
              );
            },
          }}
          render={({ field }) => (
            <DateTimePicker
              value={field.value}
              onChange={field.onChange}
              minDate={fromLocalString(watch("pickupDateTime"))}
              placeholder="Select dropoff date & time"
              className={fieldClass(!!errors.dropoffTime)}
              hasError={!!errors.dropoffTime}
            />
          )}
        />
      </FormField>
    </>
  );
}

// Optional return leg: picks the member back up from the outbound dropoff
// and takes them back to the outbound pickup. When "Will Call" is checked,
// the exact pickup time isn't known yet (the facility calls dispatch when
// the member is ready), so the time is pinned to 23:59:59 on the chosen date
// (see buildReturnTripLeg in tripPayload.js, which also mirrors this into
// the leg's appointment time).
function ReturnTripSection({ control, setValue, watch, errors, showHeader, serviceAreas }) {
  const isReturnTrip = watch("isReturnTrip");
  const isWillCall = watch("returnIsWillCall");
  const dropoffTime = watch("dropoffTime");
  const returnPickupDateTime = watch("returnPickupDateTime");

  function handleToggle(checked) {
    setValue("isReturnTrip", checked);
    if (!checked) return;

    if (!watch("returnPickupAddress")) {
      setValue("returnPickupAddress", watch("dropoffAddress") || "");
      setValue("returnPickupAddressLat", watch("dropoffAddressLat") ?? null);
      setValue("returnPickupAddressLng", watch("dropoffAddressLng") ?? null);
      setValue("returnPickupAddressZip", watch("dropoffAddressZip") || "");
      setValue("returnServiceArea", watch("serviceAreaDest") ?? null);
    }
    if (!watch("returnDropoffAddress")) {
      setValue("returnDropoffAddress", watch("pickupAddress") || "");
      setValue("returnDropoffAddressLat", watch("pickupAddressLat") ?? null);
      setValue("returnDropoffAddressLng", watch("pickupAddressLng") ?? null);
      setValue("returnDropoffAddressZip", watch("pickupAddressZip") || "");
      setValue("returnServiceAreaDest", watch("serviceArea") ?? null);
    }
  }

  // Keeps the return pickup in step with the outbound dropoff: if it's
  // unset, or the outbound pickup/dropoff is later moved past it, push it
  // forward so staff can never end up with a return pickup before the
  // outbound dropoff. Leaves it alone otherwise, so a manually chosen later
  // return time isn't clobbered on every keystroke.
  useEffect(() => {
    if (!isReturnTrip || !dropoffTime) return;
    if (returnPickupDateTime && returnPickupDateTime >= dropoffTime) return;

    setValue(
      "returnPickupDateTime",
      isWillCall
        ? `${dropoffTime.slice(0, 10)}T23:59`
        : addHoursToLocalString(dropoffTime, 1)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReturnTrip, dropoffTime, isWillCall]);

  return (
    <>
      {showHeader && <SectionHeader title="Return Trip" />}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Add a return trip
        </span>
        <Switch checked={!!isReturnTrip} onChange={handleToggle} color="dark/zinc" />
      </div>

      {isReturnTrip && (
        <>
          <FormField
            label="Return Pickup Address"
            required
            error={errors.returnPickupAddress?.message}
          >
            <Controller
              name="returnPickupAddress"
              control={control}
              rules={{
                required: "Address is required",
                validate: () =>
                  (watch("returnPickupAddressLat") != null && watch("returnPickupAddressLng") != null) ||
                  "Please select an address from the dropdown",
              }}
              render={({ field }) => (
                <AddressAutocompleteInput
                  value={field.value}
                  lat={watch("returnPickupAddressLat")}
                  lng={watch("returnPickupAddressLng")}
                  onChange={({ address, lat, lng, components }) => {
                    setValue("returnPickupAddressLat", lat);
                    setValue("returnPickupAddressLng", lng);
                    setValue("returnPickupAddressZip", components?.zipCode || "");
                    setValue("returnServiceArea", findServiceAreaId(components?.zipCode, serviceAreas));
                    field.onChange(address);
                  }}
                  placeholder="Enter a location"
                  className={fieldClass(!!errors.returnPickupAddress)}
                />
              )}
            />
          </FormField>

          <FormField
            label="Return Pickup Date & Time"
            required
            error={errors.returnPickupDateTime?.message}
          >
            <Controller
              name="returnPickupDateTime"
              control={control}
              rules={{
                required: "Return pickup time is required",
                validate: (value) => {
                  if (!value || !dropoffTime) return true;
                  return (
                    value >= dropoffTime ||
                    "Return pickup time can't be before dropoff time"
                  );
                },
              }}
              render={({ field }) => (
                <DateTimePicker
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isWillCall}
                  minDate={fromLocalString(dropoffTime)}
                  placeholder="Select return pickup date & time"
                  className={fieldClass(!!errors.returnPickupDateTime)}
                  hasError={!!errors.returnPickupDateTime}
                />
              )}
            />
          </FormField>

          <label className="flex items-center gap-2 -mt-2">
            <Controller
              name="returnIsWillCall"
              control={control}
              render={({ field }) => (
                <input
                  type="checkbox"
                  checked={!!field.value}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    field.onChange(checked);
                    if (!checked) return;
                    const current = watch("returnPickupDateTime") || toLocalString(new Date());
                    setValue("returnPickupDateTime", `${current.slice(0, 10)}T23:59`);
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-[#293562] focus:ring-[#293562]"
                />
              )}
            />
            <span className="text-sm font-medium text-gray-700">Will Call</span>
          </label>

          <FormField
            label="Return Dropoff Address"
            required
            error={errors.returnDropoffAddress?.message}
          >
            <Controller
              name="returnDropoffAddress"
              control={control}
              rules={{
                required: "Address is required",
                validate: () =>
                  (watch("returnDropoffAddressLat") != null && watch("returnDropoffAddressLng") != null) ||
                  "Please select an address from the dropdown",
              }}
              render={({ field }) => (
                <AddressAutocompleteInput
                  value={field.value}
                  lat={watch("returnDropoffAddressLat")}
                  lng={watch("returnDropoffAddressLng")}
                  onChange={({ address, lat, lng, components }) => {
                    setValue("returnDropoffAddressLat", lat);
                    setValue("returnDropoffAddressLng", lng);
                    setValue("returnDropoffAddressZip", components?.zipCode || "");
                    setValue("returnServiceAreaDest", findServiceAreaId(components?.zipCode, serviceAreas));
                    field.onChange(address);
                  }}
                  placeholder="Enter a location"
                  className={fieldClass(!!errors.returnDropoffAddress)}
                />
              )}
            />
          </FormField>
        </>
      )}
    </>
  );
}

function NotesSection({ register, showHeader }) {
  return (
    <>
      {showHeader && <SectionHeader title="Notes" />}
      <FormField label="Driver Notes">
        <textarea
          rows={2}
          {...register("driverNotes")}
          className={`${fieldClass(false)} resize-none`}
        />
      </FormField>
    </>
  );
}

export default function CreateTripPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fundingsourceId, memberId } = useParams();
  const timeZone = location.state?.timeZone ?? null;
  const member = location.state?.member ?? null;
  const searchBy = location.state?.searchBy ?? "search";
  const [fundingSource, setFundingSource] = useState(
    location.state?.fundingSource ?? null
  );

  const {
    register,
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: buildDefaultValues(member, timeZone) });

  const [configs, setConfigs] = useState({
    serviceAreas: [],
    levelOfServices: [],
    spaceTypes: [],
  });
  const [configsLoading, setConfigsLoading] = useState(true);

  useEffect(() => {
    if (!fundingsourceId) return;

    let cancelled = false;

    fetchConfigurations(fundingsourceId)
      .then((data) => {
        if (!cancelled) {
          setConfigs(data);
          if (member?.addressZip) {
            const saId = findServiceAreaId(member.addressZip, data.serviceAreas);
            setValue("serviceArea", saId);
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          ToastError(error.message || "Unable to load configurations");
        }
      })
      .finally(() => {
        if (!cancelled) setConfigsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fundingsourceId, member, setValue]);

  // Falls back to fetching the funding source directly on direct links or
  // refreshes, where the TripsListing page's navigation state (which
  // normally carries it) isn't available.
  useEffect(() => {
    if (!fundingsourceId) return;
    let cancelled = false;
    fetchCooperateDetails(fundingsourceId)
      .then((data) => {
        if (!cancelled) setFundingSource(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [fundingsourceId]);

  const spaceTypeOptions = configs.spaceTypes.map((item) => ({
    value: item._id,
    label: item.title,
  }));

  async function onSubmit(values) {
    try {
      const st = configs.spaceTypes.find((s) => s._id === values.spaceType);
      const levelOfService = st ? st.los : "";
      const valuesWithLos = { ...values, levelOfService };

      const payload = buildCreateTripPayload(valuesWithLos, {
        fundingsourceId,
        memberId,
        timeZone,
        client: member?.id ?? null,
        gender: member?.gender || "",
      });
      await createTrip(payload, fundingsourceId);

      navigate(`/fundingsource/${fundingsourceId}/trips/${memberId}`, {
        state: { searchBy, fundingSource },
      });
    } catch (err) {
      ToastError(err.message || "Unable to create trip. Please try again.");
    }
  }

  return (
    <section className="min-h-dvh bg-gradient-to-br from-[#eef1f8] via-[#f5f5f7] to-[#e9ecf3] flex items-center justify-center p-4">
      <div className="w-full max-w-lg h-[calc(100dvh-2rem)] bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_10px_40px_rgb(0,0,0,0.08)] ring-1 ring-black/5 flex flex-col overflow-hidden">

        {/* Fixed top */}
        <div className="shrink-0 px-6 pt-4 pb-3 space-y-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">New Trip</h2>
        </div>

        {/* Form: flex column so fields scroll and button stays fixed */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex-1 flex flex-col min-h-0 overflow-hidden"
          noValidate
        >
          {/* Scrollable fields */}
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            <ServiceSection
              register={register}
              errors={errors}
              spaceTypeOptions={spaceTypeOptions}
              configsLoading={configsLoading}
              showAuthorizationNumber={!!fundingSource?.allowAuthorizationNumber}
            />
            <PickupSection
              control={control}
              setValue={setValue}
              watch={watch}
              errors={errors}
              serviceAreas={configs.serviceAreas}
            />
            <DropoffSection
              control={control}
              setValue={setValue}
              watch={watch}
              errors={errors}
              serviceAreas={configs.serviceAreas}
            />
            <ReturnTripSection
              control={control}
              setValue={setValue}
              watch={watch}
              errors={errors}
              serviceAreas={configs.serviceAreas}
              showHeader
            />
            <NotesSection register={register} />
          </div>

          {/* Fixed bottom button */}
          <div className="shrink-0 px-6 pb-6 pt-3 border-t border-gray-100">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-2xl bg-[#293562] py-4 text-lg font-semibold text-white shadow-sm hover:brightness-95 transition disabled:opacity-60"
            >
              {isSubmitting ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
